import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { adminFetch, exportCsv } from '@/adminApi';
import { useAdmin } from '@/contexts/AdminContext';

interface Payment {
  id: string;
  user_id: string;
  email: string;
  full_name: string | null;
  amount: string;
  currency: string;
  status: string;
  provider: string;
  provider_payment_id: string | null;
  subscription_tier: string | null;
  subscription_plan: string | null;
  created_at: string;
  billing_period?: string;
  payment_type?: string;
  display_tier?: string;
  display_billing_period?: string;
  metadata?: Record<string, unknown>;
}

export default function AdminPaymentsPage() {
  const { isAuthenticated } = useAdmin();
  const navigate = useNavigate();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [paymentTypeFilter, setPaymentTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Detail modal
  const [detailModal, setDetailModal] = useState<Payment | null>(null);

  // Toast
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const toastRef = useRef<ReturnType<typeof setTimeout>>();
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    if (toastRef.current) clearTimeout(toastRef.current);
    toastRef.current = setTimeout(() => setToast(null), 3000);
  };

  // Debounce
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [debouncedSearch, statusFilter, tierFilter, paymentTypeFilter]);

  const loadPayments = useCallback(async () => {
    if (!isAuthenticated) { navigate('/admin'); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(), limit: '50',
        ...(statusFilter && { status: statusFilter }),
        ...(tierFilter && { tier: tierFilter }),
        ...(paymentTypeFilter && { payment_type: paymentTypeFilter }),
        ...(debouncedSearch && { search: debouncedSearch }),
      });
      const data = await adminFetch<{ payments: Payment[]; total: number }>('GET', `/api/admin/payments?${params}`);
      setPayments(data.payments);
      setTotal(data.total);
    } catch { setPayments([]); }
    finally { setLoading(false); }
  }, [page, statusFilter, tierFilter, paymentTypeFilter, debouncedSearch, isAuthenticated, navigate]);

  useEffect(() => { loadPayments(); }, [loadPayments]);

  const handleExport = async () => {
    try {
      const res = await exportCsv('payments', { limit: 10000 });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payments-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      showToast('CSV экспортирован');
    } catch (err) {
      showToast('Ошибка экспорта: ' + (err instanceof Error ? err.message : ''), 'error');
    }
  };

  const totalPages = Math.ceil(total / 50);
  const statusBadge = (s: string) => {
    const c: Record<string, string> = { succeeded: 'bg-green-600/30 text-green-300', pending: 'bg-amber-600/30 text-amber-300', failed: 'bg-red-600/30 text-red-300', refunded: 'bg-slate-600/30 text-slate-300' };
    return c[s] || 'bg-slate-600 text-slate-200';
  };
  const statusLabel = (s: string) => {
    const l: Record<string, string> = { succeeded: 'Успешно', pending: 'В обработке', failed: 'Ошибка', refunded: 'Возврат' };
    return l[s] || s;
  };
  const tierBadge = (t: string) => {
    const c: Record<string, string> = { base: 'bg-teal-600/30 text-teal-300', premium: 'bg-purple-600/30 text-purple-300', pro: 'bg-amber-600/30 text-amber-300' };
    return c[t] || 'bg-slate-600 text-slate-200';
  };
  const typeBadge = (t?: string) => {
    if (!t) return null;
    const c: Record<string, string> = { new_subscription: 'bg-teal-600/20 text-teal-400', plan_change: 'bg-amber-600/20 text-amber-400', one_time: 'bg-purple-600/20 text-purple-400' };
    const l: Record<string, string> = { new_subscription: 'Новая', plan_change: 'Смена', one_time: 'Разовая' };
    return <span className={`px-2 py-0.5 rounded text-xs font-medium ${c[t] || 'bg-slate-600 text-slate-200'}`}>{l[t] || t}</span>;
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {toast && <div className={`fixed top-4 right-4 z-[60] px-4 py-3 rounded-lg text-sm font-medium shadow-lg ${toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>{toast.message}</div>}

      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/admin/dashboard" className="text-lg font-bold text-white hover:text-purple-400 transition">← Назад</Link>
          <h1 className="text-lg font-bold text-white">Платежи ({total})</h1>
          <button onClick={handleExport} className="px-3 py-1.5 text-sm bg-green-600/20 text-green-400 rounded-lg hover:bg-green-600/40 transition">📥 Экспорт CSV</button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Поиск по email..."
            className="flex-1 min-w-48 px-4 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500" />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500">
            <option value="">Все статусы</option>
            <option value="succeeded">Успешные</option>
            <option value="pending">В обработке</option>
            <option value="failed">Ошибки</option>
            <option value="refunded">Возвраты</option>
          </select>
          <select value={tierFilter} onChange={(e) => setTierFilter(e.target.value)}
            className="px-3 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500">
            <option value="">Все тарифы</option>
            <option value="base">Base</option>
            <option value="premium">Premium</option>
            <option value="pro">Pro</option>
          </select>
          <select value={paymentTypeFilter} onChange={(e) => setPaymentTypeFilter(e.target.value)}
            className="px-3 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500">
            <option value="">Все типы</option>
            <option value="new_subscription">Новая подписка</option>
            <option value="plan_change">Смена тарифа</option>
            <option value="one_time">Разовая покупка</option>
          </select>
        </div>

        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          {loading ? <div className="px-6 py-12 text-center text-slate-500">Загрузка...</div> : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-slate-400 text-sm border-b border-slate-700">
                      <th className="px-4 py-3 font-medium">Дата</th>
                      <th className="px-4 py-3 font-medium">Email</th>
                      <th className="px-4 py-3 font-medium">Сумма</th>
                      <th className="px-4 py-3 font-medium">Тариф</th>
                      <th className="px-4 py-3 font-medium">Тип</th>
                      <th className="px-4 py-3 font-medium">Статус</th>
                      <th className="px-4 py-3 font-medium">Провайдер</th>
                      <th className="px-4 py-3 font-medium">Детали</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.length === 0 ? (
                      <tr><td colSpan={8} className="px-6 py-8 text-center text-slate-500">Платежи не найдены</td></tr>
                    ) : payments.map((p) => (
                      <tr key={p.id} className="border-b border-slate-700/50 hover:bg-slate-700/30 transition">
                        <td className="px-4 py-3 text-slate-400 text-xs">{new Date(p.created_at).toLocaleDateString('ru-RU')}</td>
                        <td className="px-4 py-3"><Link to={`/admin/users/${p.user_id}`} className="text-blue-400 hover:underline text-sm">{p.email}</Link></td>
                        <td className="px-4 py-3 text-white font-semibold text-sm">{p.amount} {p.currency}</td>
                        <td className="px-4 py-3">
                          {p.display_tier && p.display_tier !== '—' ? (
                            <div>
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${tierBadge(p.display_tier)}`}>{p.display_tier}</span>
                              {p.display_billing_period && <div className="text-slate-500 text-xs mt-0.5">{p.display_billing_period}</div>}
                            </div>
                          ) : '—'}
                        </td>
                        <td className="px-4 py-3">{typeBadge(p.payment_type)}</td>
                        <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded text-xs font-medium ${statusBadge(p.status)}`}>{statusLabel(p.status)}</span></td>
                        <td className="px-4 py-3 text-slate-500 text-xs font-mono">{p.provider || '—'}</td>
                        <td className="px-4 py-3">
                          <button onClick={() => setDetailModal(p)} className="text-xs px-2 py-1 bg-slate-700/50 text-slate-300 rounded hover:bg-slate-600 transition">Подробнее</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-slate-700 flex items-center justify-between">
                  <span className="text-sm text-slate-400">Страница {page} из {totalPages}</span>
                  <div className="flex gap-2">
                    <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded disabled:opacity-50 hover:bg-slate-600 transition">←</button>
                    <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded disabled:opacity-50 hover:bg-slate-600 transition">→</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Detail Modal */}
      {detailModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 w-full max-w-lg">
            <h2 className="text-lg font-bold text-white mb-4">Детали платежа</h2>
            <dl className="space-y-3">
              {[
                { label: 'ID', value: detailModal.id },
                { label: 'Email', value: detailModal.email },
                { label: 'Имя', value: detailModal.full_name || '—' },
                { label: 'Сумма', value: `${detailModal.amount} ${detailModal.currency}` },
                { label: 'Тариф', value: detailModal.display_tier || '—' },
                { label: 'Период', value: detailModal.display_billing_period || '—' },
                { label: 'Тип', value: detailModal.payment_type || '—' },
                { label: 'Статус', value: statusLabel(detailModal.status) },
                { label: 'Провайдер', value: detailModal.provider || '—' },
                { label: 'ID провайдера', value: detailModal.provider_payment_id || '—' },
                { label: 'Дата', value: new Date(detailModal.created_at).toLocaleString('ru-RU') },
              ].map(({ label, value }) => (
                <div key={label} className="flex justify-between">
                  <dt className="text-sm text-slate-400">{label}</dt>
                  <dd className="text-sm text-white font-mono">{value}</dd>
                </div>
              ))}
            </dl>
            {detailModal.metadata && Object.keys(detailModal.metadata).length > 0 && (
              <div className="mt-4 p-3 bg-slate-900/50 rounded-lg">
                <div className="text-xs text-slate-500 mb-2">Метаданные</div>
                <pre className="text-xs text-slate-300 overflow-x-auto">{JSON.stringify(detailModal.metadata, null, 2)}</pre>
              </div>
            )}
            <div className="flex justify-end mt-5">
              <button onClick={() => setDetailModal(null)} className="px-4 py-2 text-sm bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition">Закрыть</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
